import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApp } from "@/context/AppContext";
import { formatSAR } from "@/i18n";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Snowflake, Loader2, Lock, Eye, EyeOff, RefreshCw, KeyRound, ShieldCheck, Smartphone, Check } from "lucide-react";

const LIMIT_OPTIONS = ["0", "500", "1000", "2000", "5000", "10000"];
const MOBILE_WALLETS = [
  { id: "apple", labelKey: "applePay" },
  { id: "mada", labelKey: "madaPay" },
  { id: "samsung", labelKey: "samsungWallet" },
];
const PinInput = (props) => (
  <Input type="password" inputMode="numeric" maxLength={4}
    className="h-12 rounded-2xl text-center text-xl tracking-[0.5em] font-bold" {...props} />
);

export default function CardControlsModal({ open, onClose, onSuccess }) {
  const { t, lang } = useApp();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [pin, setPin] = useState("");            // active verified pin (session)
  const [pinEntry, setPinEntry] = useState("");  // unlock field
  const [reveal, setReveal] = useState(null);
  const [pinForm, setPinForm] = useState(null);  // {current,next,confirm}
  const [confirmReissue, setConfirmReissue] = useState(false);

  const load = () => api.get("/wallet/card").then((r) => setCard(r.data)).catch(() => {});

  useEffect(() => {
    if (open) {
      setCard(null); setUnlocked(false); setPin(""); setPinEntry("");
      setReveal(null); setPinForm(null); setConfirmReissue(false);
      load();
    }
  }, [open]);

  const needsUnlock = card?.pin_set && !unlocked;

  const callUpdate = async (payload) => {
    setLoading(true);
    try {
      const res = await api.put("/wallet/card", { ...payload, pin: card.pin_set ? pin : undefined });
      setCard(res.data);
      toast.success(t.toastCardUpdated);
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  const unlock = async () => {
    setLoading(true);
    try {
      await api.put("/wallet/card", { pin: pinEntry });
      setPin(pinEntry); setUnlocked(true); setPinEntry("");
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  const savePin = async () => {
    if (pinForm.next !== pinForm.confirm) { toast.error(t.pinMismatch); return; }
    setLoading(true);
    try {
      await api.post("/wallet/card/pin", { pin: pinForm.next, current_pin: pinForm.current });
      toast.success(t.toastPinSet);
      setPin(pinForm.next); setUnlocked(true); setPinForm(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  const doReveal = async () => {
    setLoading(true);
    try {
      const r = await api.post("/wallet/card/reveal", { pin: card.pin_set ? pin : undefined });
      setReveal(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  const doReissue = async () => {
    setLoading(true);
    try {
      await api.post("/wallet/card/reissue", { pin: card.pin_set ? pin : undefined });
      toast.success(t.toastReissued);
      setConfirmReissue(false); setReveal(null);
      await load(); onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  const toggleWallet = async (walletId) => {
    const linked = card.linked_wallets?.includes(walletId);
    setLoading(true);
    try {
      const endpoint = linked ? "/wallet/card/unlink-wallet" : "/wallet/card/link-wallet";
      const res = await api.post(endpoint, { wallet: walletId, pin: card.pin_set ? pin : undefined });
      setCard({ ...card, linked_wallets: res.data.linked_wallets });
      if (!linked) toast.success(t.toastWalletLinked);
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="card-controls-modal" className="max-w-sm rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" /> {t.cardControls}
          </DialogTitle>
          <DialogDescription>{t.cardControlsDesc}</DialogDescription>
        </DialogHeader>

        {!card ? (
          <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : needsUnlock ? (
          <div className="space-y-4 pt-2 text-center" data-testid="card-unlock-view">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 grid place-items-center mx-auto">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">{t.unlockCard}</p>
            <PinInput data-testid="card-unlock-pin" value={pinEntry} onChange={(e) => setPinEntry(e.target.value.replace(/\D/g, ""))} placeholder="••••" />
            <Button data-testid="card-unlock-btn" onClick={unlock} disabled={loading || pinEntry.length !== 4} className="w-full h-12 rounded-2xl font-bold">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t.unlockCard.split(" ")[0]}
            </Button>
          </div>
        ) : pinForm ? (
          <div className="space-y-3.5 pt-1" data-testid="card-pin-form">
            {card.pin_set && (
              <div>
                <label className="text-xs font-semibold text-muted-foreground">{t.currentPin}</label>
                <PinInput data-testid="pin-current" value={pinForm.current} onChange={(e) => setPinForm({ ...pinForm, current: e.target.value.replace(/\D/g, "") })} />
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-muted-foreground">{t.newPin}</label>
              <PinInput data-testid="pin-new" value={pinForm.next} onChange={(e) => setPinForm({ ...pinForm, next: e.target.value.replace(/\D/g, "") })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">{t.confirmPin}</label>
              <PinInput data-testid="pin-confirm" value={pinForm.confirm} onChange={(e) => setPinForm({ ...pinForm, confirm: e.target.value.replace(/\D/g, "") })} />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPinForm(null)} className="flex-1 h-12 rounded-2xl">{t.cancel}</Button>
              <Button data-testid="pin-save-btn" onClick={savePin} disabled={loading || pinForm.next.length !== 4} className="flex-1 h-12 rounded-2xl font-bold">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t.save}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3.5 pt-1">
            <div data-testid="card-controls-visual" className={`relative overflow-hidden rounded-2xl p-4 text-white ${card.frozen ? "bg-gradient-to-br from-slate-700 to-slate-500" : "bg-gradient-to-br from-slate-900 via-emerald-950 to-emerald-800"}`}>
              <div className="flex items-center justify-between">
                <span className="font-extrabold tracking-tight">Sb<span className="text-emerald-300">Pay</span></span>
                {card.frozen && <span className="flex items-center gap-1 text-xs font-bold bg-white/15 px-2 py-0.5 rounded-full"><Snowflake className="w-3.5 h-3.5" /> {t.cardFrozen}</span>}
              </div>
              <p className="font-mono text-base tracking-[0.2em] text-white/90 mt-5">{reveal ? reveal.number : `•••• •••• •••• ${card.last4}`}</p>
              {reveal ? (
                <div className="flex gap-5 text-xs text-white/70 mt-1 font-mono">
                  <span>{t.expiry}: {reveal.exp}</span><span>{t.cvv}: {reveal.cvv}</span>
                </div>
              ) : <p className="text-xs text-white/60 mt-1">@{card.handle}</p>}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" data-testid="card-reveal-btn" onClick={() => (reveal ? setReveal(null) : doReveal())} disabled={loading} className="flex-1 h-11 rounded-2xl text-sm">
                {reveal ? <><EyeOff className="w-4 h-4 me-1.5" />{t.hideDetails}</> : <><Eye className="w-4 h-4 me-1.5" />{t.revealCard}</>}
              </Button>
              <Button variant="outline" data-testid="card-reissue-btn" onClick={() => setConfirmReissue(true)} disabled={loading} className="flex-1 h-11 rounded-2xl text-sm">
                <RefreshCw className="w-4 h-4 me-1.5" />{t.reissueCard}
              </Button>
            </div>

            {confirmReissue && (
              <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3.5 space-y-2.5" data-testid="reissue-confirm">
                <p className="text-sm">{t.reissueConfirm}</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setConfirmReissue(false)} className="flex-1 h-10 rounded-xl">{t.cancel}</Button>
                  <Button data-testid="reissue-confirm-btn" onClick={doReissue} disabled={loading} className="flex-1 h-10 rounded-xl font-bold">{t.reissueCard}</Button>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between p-4 rounded-2xl bg-muted/50 border border-border">
              <div className="min-w-0 pe-2">
                <p className="text-sm font-bold">{t.freezeCard}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t.freezeCardDesc}</p>
              </div>
              <Switch data-testid="card-freeze-toggle" checked={card.frozen} disabled={loading} onCheckedChange={(v) => callUpdate({ frozen: v })} />
            </div>

            <div className="p-4 rounded-2xl bg-muted/50 border border-border">
              <p className="text-sm font-bold">{t.dailyLimit}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t.dailyLimitDesc}</p>
              <Select value={String(card.daily_limit || 0)} onValueChange={(v) => callUpdate({ daily_limit: parseFloat(v) })} disabled={loading}>
                <SelectTrigger data-testid="card-limit-select" className="h-11 rounded-2xl mt-2.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LIMIT_OPTIONS.map((a) => (
                    <SelectItem key={a} value={a} data-testid={`card-limit-${a}`}>
                      {a === "0" ? t.noLimit : `${Number(a).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")} ${t.currency}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {card.daily_limit > 0 && (
                <div className="flex items-center justify-between mt-3 text-xs">
                  <span className="text-muted-foreground">{t.spentToday}: <span className="font-bold text-foreground tabular-nums">{formatSAR(card.spent_today, lang)}</span></span>
                  <span className="text-muted-foreground">{t.remainingToday}: <span className="font-bold text-primary tabular-nums">{formatSAR(card.remaining_today, lang)}</span></span>
                </div>
              )}
            </div>

            <button data-testid="card-pin-manage-btn" onClick={() => setPinForm({ current: "", next: "", confirm: "" })} className="w-full flex items-center justify-between p-4 rounded-2xl bg-muted/50 border border-border hover:bg-muted transition-colors">
              <span className="flex items-center gap-2.5 text-sm font-bold">
                <KeyRound className="w-4 h-4 text-primary" />{card.pin_set ? t.changePin : t.setPin}
              </span>
              {card.pin_set && <span className="flex items-center gap-1 text-xs text-emerald-600 font-semibold"><ShieldCheck className="w-3.5 h-3.5" />{t.pinProtected}</span>}
            </button>

            <div className="pt-1" data-testid="add-to-wallet-section">
              <p className="text-sm font-bold flex items-center gap-2 mb-2.5"><Smartphone className="w-4 h-4 text-primary" />{t.addToWallet}</p>
              <div className="grid grid-cols-3 gap-2">
                {MOBILE_WALLETS.map((w) => {
                  const linked = card.linked_wallets?.includes(w.id);
                  return (
                    <button
                      key={w.id}
                      data-testid={`wallet-link-${w.id}`}
                      onClick={() => toggleWallet(w.id)}
                      disabled={loading}
                      className={`relative flex flex-col items-center gap-1.5 py-3 px-1 rounded-2xl border text-xs font-bold transition-all ${linked ? "border-primary bg-primary/10 text-primary" : "border-border bg-card hover:border-primary/40"}`}
                    >
                      {linked && <Check className="w-3.5 h-3.5 absolute top-1.5 end-1.5" />}
                      <Smartphone className="w-5 h-5" />
                      <span className="leading-tight text-center">{t[w.labelKey]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
